import React from 'react';
import {
  ApplicationActionType,
  applicationReducer,
  CacheContext,
  FeesService,
  IFeesService,
  ISalesforceService,
  SalesforceService,
} from 'src/features/common';
import { DeviceActivationApp } from 'src/features/device-activation/app/device-activation.app';
import { pageNameMap as deviceActivationPageMap } from 'src/features/device-activation/pages/page-name-map';
import { useDeepCompareEffect } from 'src/hooks/useDeepCompareEffect';
import { useHistory } from 'react-router-dom';
import { deviceActivationRoute } from 'src/routing/routes';
import { AppCacheKeys } from 'src/types';
import {
  DeviceActivationTealiumService,
  IDeviceActivationTealiumService,
} from 'src/services/tealium/device-activation/device-activation-tealium-service';
import DpoTealiumService, {
  IDpoTealiumService,
} from 'src/services/tealium/dpo/dpo-tealium-service';
import { JOURNEY_NAME, VISITOR_CUSTOMER_TYPE } from 'src/features/common/types/tealium-service';

const OnboardingAppContainer = React.lazy(
  () => import('src/features/onboarding/app/onboarding.container'),
);

export function DeviceActivationAppContainer() {
  const cacheStore = React.useContext(CacheContext);
  const history = useHistory();
  const basePath = deviceActivationRoute;
  const APPLICATION_CACHE_KEY = AppCacheKeys.deviceActivation;
  const [application, dispatch] = React.useReducer(
    applicationReducer,
    cacheStore.get(APPLICATION_CACHE_KEY),
  );

  const salesforceService: React.MutableRefObject<ISalesforceService> =
    React.useRef<ISalesforceService>(new SalesforceService(cacheStore));
  const feesService: React.MutableRefObject<IFeesService> = React.useRef<IFeesService>(
    new FeesService(basePath, cacheStore),
  );

  const deviceActivationTealiumService: React.MutableRefObject<IDeviceActivationTealiumService> =
    React.useRef<IDeviceActivationTealiumService>(new DeviceActivationTealiumService());

  const dpoTealiumService: React.MutableRefObject<IDpoTealiumService> =
    React.useRef<IDpoTealiumService>(
      new DpoTealiumService(JOURNEY_NAME.DEVICE_ACTIVATION, VISITOR_CUSTOMER_TYPE.CONSUMER),
    );

  useDeepCompareEffect(() => {
    if (application && application.isOTPLogin) {
      dispatch({
        type: ApplicationActionType.SET_CONTINUE_WITH_OTP_APP,
        application: application,
        remotePaymentsEnabled: false,
      });

      return;
    }

    if (!application) {
      dispatch({ type: ApplicationActionType.DEVICE_ACTIVATION });
    } else {
      cacheStore.set(APPLICATION_CACHE_KEY, application);
    }
  }, [application]);

  useDeepCompareEffect(() => {
    if (application?.steps !== undefined && application?.currentStepIndex !== undefined) {
      const step = application.steps[application.currentStepIndex];
      const routePath = `${basePath}/${step.route}`;
      if (routePath !== location.pathname) {
        history.push(routePath);
      }
    }
  }, [application?.currentStepIndex]);

  if (application?.stage?.isOrderComplete) {
    return (
      <OnboardingAppContainer
        basePath={deviceActivationRoute}
        application={application}
        dispatch={dispatch}
      />
    );
  }

  return (
    <DeviceActivationApp
      pageNameMap={deviceActivationPageMap}
      dispatch={dispatch}
      basePath={deviceActivationRoute}
      application={application}
      feesService={feesService.current}
      salesforceService={salesforceService.current}
      deviceActivationTealiumService={deviceActivationTealiumService.current}
      dpoTealiumService={dpoTealiumService.current}
    />
  );
}
